import React, { useState } from "react";
import { BiEdit, BiTrash } from "react-icons/bi";
import { IoGrid } from "react-icons/io5";
import { FaCalendarAlt, FaFileInvoice } from "react-icons/fa";
import { FaTableList } from "react-icons/fa6";
import { MdDescription } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { WrittenFormat } from "../WrittenFormat";
import DeleteConfirmationModal from "../DeleteConfirmationModal/DeleteConfirmationModal";
import { CgNotes } from "react-icons/cg";
import "./style/RenderNotes.scss";

export const RenderNotes = ({
  notes = [],
  setModalTab,
  setShowModal,
  onEditNote,
  onDeleteNote,
}) => {
  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    noteId: null,
    noteTitle: "",
  });
  const [expandedContents, setExpandedContents] = useState({});
  const [viewMode, setViewMode] = useState("table");

  // Helper function to truncate text
  const truncateText = (text, wordLimit = 20, charLimit = 100) => {
    if (!text) return "";
    const words = text.split(/\s+/);
    if (words.length <= wordLimit && text.length <= charLimit) return text;
    let truncated = words.slice(0, wordLimit).join(" ");
    if (truncated.length > charLimit) {
      truncated = truncated.slice(0, charLimit).replace(/\s+\S*$/, "");
    }
    return truncated + "...";
  };

  const getWordCount = (text) => (text ? text.split(/\s+/).filter((w) => w.length > 0).length : 0);
  const getCharCount = (text) => (text ? text.length : 0);

  const toggleContent = (noteId, e) => {
    e.stopPropagation();
    setExpandedContents((prev) => ({ ...prev, [noteId]: !prev[noteId] }));
  };

  const handleDeleteClick = (noteId, noteTitle) => {
    setDeleteModal({ isOpen: true, noteId, noteTitle });
  };

  const handleConfirmDelete = async () => {
    await onDeleteNote(deleteModal.noteId);
    setDeleteModal({ isOpen: false, noteId: null, noteTitle: "" });
  };

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, noteId: null, noteTitle: "" });
  };

  const handleNoteClick = (noteId) => {
    navigate(`/home/notes/details/${noteId}`);
  };

  const handleEditClick = (note) => {
    setModalTab("Notes");
    setShowModal(true);
    onEditNote(note);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
    });
  };

  // --- Renderers ---

  // Table View
  const renderTableView = () => (
    <div className="table-responsive">
      <table className="documents-table">
        <thead>
          <tr>
            <th className="td-th-casedet">Title</th>
            <th className="td-th-casedet">Created At</th>
            <th className="td-th-casedet">Court Ref</th>
            <th style={{ textAlign: "center" }} className="td-th-casedet">Actions</th>
          </tr>
        </thead>
        <tbody>
          {notes.map((note, index) => (
            <tr
              key={index}
              onClick={() => handleNoteClick(note._id)}
              style={{ cursor: "pointer" }}
              title="Click to view details"
            >
              <td className="td-th-casedet">{WrittenFormat(note.title)}</td>
              <td className="td-th-casedet">{formatDate(note.createdAt)}</td>
              <td className="td-th-casedet">{note.courtRef}</td>
              <td className="td-th-casedet">
                <div className="editdel-table-actions">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick(note);
                    }}
                    className="editbtn"
                    title="Update"
                  >
                    <BiEdit size={16} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(note._id, note.title);
                    }}
                    className="delbtn"
                    title="Delete"
                  >
                    <BiTrash size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Modern Grid View
  const renderGridView = () => (
    <div className="notes-grid-layout">
      {notes.map((note, index) => (
        <div
          key={index}
          className="modern-note-card"
          onClick={() => handleNoteClick(note._id)}
          style={{ cursor: "pointer" }}
        >
          {/* Header Strip */}
          <div className="card-header-strip">
            <div className="header-left">
              <div className="note-title-badge">
                <CgNotes className="type-icon" />
                <span className="title-text">{WrittenFormat(note.title)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="header-actions">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(note);
                }}
                className="action-btn edit-btn"
                title="Edit"
              >
                <BiEdit size={16} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(note._id, note.title);
                }}
                className="action-btn delete-btn"
                title="Delete"
              >
                <BiTrash size={16} />
              </button>
            </div>
          </div>

          {/* Content Body */}
          <div className="card-content-body">
            {/* First Row - Created At and Court Reference in same row */}
            <div className="detail-row-horizontal">
              <div className="detail-row detail-row-half">
                <div className="detail-label">
                  <FaCalendarAlt className="row-icon" />
                  Created At
                </div>
                <div className="detail-value">{formatDate(note.createdAt)}</div>
              </div>
              <div className="detail-row detail-row-half">
                <div className="detail-label">
                  <FaFileInvoice className="row-icon" />
                  Court Ref
                </div>
                <div className="detail-value">{note.courtRef || "N/A"}</div>
              </div>
            </div>

            {/* Content Row */}
            <div className="detail-row">
              <div className="detail-label">
                <MdDescription className="row-icon" />
                Content
              </div>
              <div className="detail-value description-text">
                {expandedContents[note._id]
                  ? note.content
                  : getWordCount(note.content) > 20 || getCharCount(note.content) > 120
                  ? truncateText(note.content, 20, 120)
                  : note.content}
                
                {(getWordCount(note.content) > 20 || getCharCount(note.content) > 120) && (
                  <button
                    className="read-more-btn"
                    onClick={(e) => toggleContent(note._id, e)}
                  >
                    {expandedContents[note._id] ? "Show Less" : "Read More"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="tab-content">
      <div className="summary header-actions-container">
        <div className="sum-amount">
          <p className="margin-zero exp-count">
            <strong>Total Notes - </strong> {notes.length}
          </p>
        </div>

        <div className="actions-right">
          {notes.length > 0 && (
            <div className="view-toggles">
              <button
                className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
                title="Grid View"
              >
                <IoGrid />
              </button>
              <button
                className={`toggle-btn ${viewMode === "table" ? "active" : ""}`}
                onClick={() => setViewMode("table")}
                title="Table View"
              >
                <FaTableList />
              </button>
            </div>
          )}

          <div className="tab-add">
            <button
              onClick={() => {
                setModalTab("Notes");
                setShowModal(true);
                onEditNote(null);
              }}
            >
              Add Note
            </button>
          </div>
        </div>
      </div>

      {notes.length === 0 ? (
        <div className="no-data-container">
          <h3 className="no-data-title">No Notes Found</h3>
          <p className="no-data-message">
            No notes have been created for this case yet. Click "Add Note" to
            create your first note.
          </p>
        </div>
      ) : viewMode === "table" ? (
        renderTableView()
      ) : (
        renderGridView()
      )}

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        itemType={`this note "${deleteModal.noteTitle}"`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};